/**
 * Analytics Portal V1 — the URL is the single source of truth for the view (Phase 9, §12.8).
 *
 * `tab` lives in the path (`/dashboard/analytics/business`), everything else in the query
 * string (`entity`, `preset`, `start`, `end`, `compare`, `category`). Back/Forward therefore
 * work for free, a reload reproduces the exact view, and a shared deep link needs no client
 * state — while the server still authorizes every request, so a link can never bypass access.
 */
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { CATEGORIES, DEFAULT_TAB, TAB_KEYS } from "@/lib/analyticsApi";

export const PORTAL_BASE = "/dashboard/analytics";
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function useAnalyticsUrlState({ presets = [], defaultPreset = "last_30_days" } = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const tab = useMemo(() => {
    const rest = location.pathname.replace(PORTAL_BASE, "").replace(/^\/+|\/+$/g, "");
    const candidate = rest.split("/")[0];
    return TAB_KEYS.includes(candidate) ? candidate : DEFAULT_TAB;   // invalid tab → Summary
  }, [location.pathname]);

  const presetKeys = presets.length ? presets.map((p) => p.key) : [defaultPreset, "custom"];
  const rawPreset = params.get("preset");
  const start = ISO_DATE.test(params.get("start") || "") ? params.get("start") : null;
  const end = ISO_DATE.test(params.get("end") || "") ? params.get("end") : null;
  const custom = Boolean(start && end);
  const preset = custom ? "custom"
    : (presetKeys.includes(rawPreset) ? rawPreset : defaultPreset);   // invalid preset → default
  const compare = params.get("compare") !== "0";
  const rawCategory = params.get("category");
  const category = CATEGORIES.includes(rawCategory) ? rawCategory : "all";
  const entity = params.get("entity") || null;

  const patch = useCallback((updates, { replace = false } = {}) => {
    setParams((current) => {
      const next = new URLSearchParams(current);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === "") next.delete(key);
        else next.set(key, String(value));
      });
      return next;
    }, { replace });
  }, [setParams]);

  const goToTab = useCallback((nextTab, extra = {}) => {
    const next = new URLSearchParams(params);
    Object.entries(extra).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") next.delete(key);
      else next.set(key, String(value));
    });
    if (nextTab !== "summary") next.delete("category");
    const suffix = nextTab === DEFAULT_TAB ? "" : `/${nextTab}`;
    const query = next.toString();
    navigate(`${PORTAL_BASE}${suffix}${query ? `?${query}` : ""}`);
  }, [navigate, params]);

  return {
    tab, entity, preset, start, end, custom, compare, category,
    range: { preset, start, end, compare },
    setEntity: (id, opts) => patch({ entity: id }, opts),
    setPreset: (key) => patch({ preset: key, start: null, end: null }),
    setCustomRange: (from, to) => patch({ start: from, end: to, preset: "custom" }),
    setCompare: (on) => patch({ compare: on ? null : "0" }),
    setCategory: (value) => patch({ category: value === "all" ? null : value }),
    goToTab,
  };
}
